import React from "react";
import { Link } from "react-router-dom";

const pillars = [
  {
    title: "Real Estate",
    description:
      "Buying, selling, and investment guidance tailored to residential property in Calgary.",
    image: "service-real-estate.jpg",
    imageAlt: "Traditional two-story home at dusk with warm exterior lighting",
  },
  {
    title: "Design & Planning",
    description:
      "3D modelling, drawing packages, and permit-ready planning for residential projects.",
    image: "service-design.png",
    imageAlt:
      "Architect reviewing floor plans on paper with a tablet and drafting tools on a desk",
  },
  {
    title: "Construction & Consulting",
    description:
      "General contracting, renovation strategy, and project management from start to finish.",
    image: "service-construction.jpg",
    imageAlt: "Residential wood framing and roof trusses under construction",
  },
];

const whyBullets = [
  "One coordinated plan from early planning through construction and closing.",
  "Clearer scope, fewer handoffs, and decisions that stay aligned across disciplines.",
  "Calgary-focused residential guidance with accountability in one place.",
];

function Home() {
  const pub = process.env.PUBLIC_URL || "";
  const heroImage = `${pub}/hero-home.jpg`;

  return (
    <div className="bg-secondary">
      <section
        className="relative flex min-h-[50vh] w-full items-center px-4 py-5 text-left text-white sm:px-6 md:min-h-[53vh] md:py-6"
        aria-labelledby="home-hero-heading"
      >
        <div
          className="absolute inset-0 bg-cover bg-no-repeat [background-position:82%_center] md:[background-position:54%_center]"
          style={{ backgroundImage: `url(${heroImage})` }}
        />
        <div
          className="absolute inset-0 bg-gradient-to-r from-black/72 from-0% via-black/24 via-[40%] to-transparent to-[58%]"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 bg-[radial-gradient(ellipse_95%_145%_at_8%_50%,rgba(0,0,0,0.42)_0%,rgba(0,0,0,0.08)_48%,transparent_62%)]"
          aria-hidden="true"
        />
        <div className="relative z-10 mx-auto w-full max-w-6xl">
          <div className="max-w-lg rounded-xl bg-black/22 px-3.5 py-3.5 sm:px-4 sm:py-4">
            <h1
              id="home-hero-heading"
              className="text-[1.32rem] font-bold leading-snug tracking-tight text-white [text-shadow:0_1px_3px_rgba(0,0,0,0.65),0_3px_14px_rgba(0,0,0,0.4)] sm:text-[1.42rem] md:text-[1.48rem] md:leading-[1.12] lg:whitespace-nowrap lg:text-[1.46rem] lg:leading-tight xl:text-[1.52rem]"
            >
              Calgary Residential Real Estate • Design • Construction
            </h1>
            <h2 className="mt-2 text-[1.04rem] font-bold leading-snug tracking-tight text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.45)] sm:text-[1.1rem] md:text-[1.14rem]">
              Aligned from Start to Finish
            </h2>
            <p className="mt-2 max-w-md text-[15px] leading-[1.5] text-white/92 [text-shadow:0_1px_2px_rgba(0,0,0,0.4)] sm:text-base sm:leading-[1.52]">
              A more integrated approach to buying, building,
              <br />
              renovating, and planning residential property.
            </p>
            <div className="mt-2.5 flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-2">
              <Link
                to="/services"
                className="inline-flex min-h-[42px] w-fit items-center justify-center rounded-md border border-white/40 bg-white/22 px-5 py-2.5 text-sm font-semibold leading-none text-white shadow-sm shadow-black/20 transition-colors duration-200 hover:border-white/48 hover:bg-white/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/70"
              >
                View Services
              </Link>
              <Link
                to="/contact"
                className="inline-flex min-h-[42px] w-fit items-center justify-center rounded-md border border-white/40 bg-white/22 px-5 py-2.5 text-sm font-semibold leading-none text-white shadow-sm shadow-black/20 transition-colors duration-200 hover:border-white/48 hover:bg-white/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/70"
              >
                Contact
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section
        className="relative z-10 -mt-4 border-t border-slate-200/70 bg-secondary pb-6 pt-2.5 md:-mt-5 md:pb-7 md:pt-2"
        aria-labelledby="pillars-heading"
      >
        <div className="container mx-auto max-w-6xl px-4">
          <h2 id="pillars-heading" className="sr-only">
            Service pillars
          </h2>
          <ul className="grid list-none gap-2.5 md:grid-cols-3 md:gap-3 lg:gap-3">
            {pillars.map((pillar) => (
              <li key={pillar.title}>
                <article className="flex h-full flex-col overflow-hidden rounded-lg bg-white shadow-[0_1px_14px_-4px_rgba(15,23,42,0.12)] ring-1 ring-slate-200/60">
                  <div className="aspect-[21/9] w-full overflow-hidden bg-slate-100">
                    <img
                      src={`${pub}/${pillar.image}`}
                      alt={pillar.imageAlt}
                      className="h-full w-full object-cover object-center brightness-[0.97] contrast-[1.02] saturate-[0.94]"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="flex flex-1 flex-col px-2.5 pb-2.5 pt-2 sm:px-3 sm:pb-3 sm:pt-2">
                    <h3 className="text-[0.9375rem] font-semibold tracking-tight text-slate-900">
                      {pillar.title}
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{pillar.description}</p>
                  </div>
                </article>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section
        className="border-t border-slate-200/60 bg-secondary pb-8 pt-5 md:pb-10 md:pt-6"
        aria-labelledby="why-heading"
      >
        <div className="container mx-auto max-w-6xl px-4">
          <div className="rounded-2xl bg-white px-5 py-5 shadow-sm ring-1 ring-slate-200/55 md:px-6 md:py-6">
            <h2
              id="why-heading"
              className="text-lg font-semibold tracking-tight text-slate-900 md:text-xl"
            >
              Why This Approach Works
            </h2>
            <p className="mt-2.5 max-w-3xl text-sm leading-relaxed text-slate-600 md:text-[15px]">
              Most clients work with separate professionals for real estate, design, and
              construction—which can create gaps in timing, scope, and accountability.
            </p>
            <ul className="mt-3 max-w-3xl list-disc space-y-1.5 pl-4 text-sm leading-relaxed text-slate-600 marker:text-slate-400 md:text-[15px]">
              {whyBullets.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
